/**
 * Home / dashboard — макет главной 29.10.2026 (владелец: расположение и вид карточек как в макете).
 *
 *  • шапка — приветствие по времени суток + сегодняшняя дата справа;
 *  • три прежние плитки: открытые задачи · непрочитанное · отпускные дни (живой остаток из 1С,
 *    фолбэк — /users/me/balances). Счётчики из макета владелец не взял;
 *  • «Дни рождения» — прежняя карточка-карусель на всю ширину, только когда есть именинники;
 *  • слева: «Требуют внимания» — сначала задачи на мне («Согласовать» открывает карточку на
 *    шаге решения, вслепую с главной не согласуем: на проде ЭДО уходит в 1С), затем мои
 *    заявки в работе; полный список — ссылкой «Все процессы». Документов один-два — под каждым
 *    маршрут согласования в линию (полный, из карточки документа). Ниже «Новости компании» —
 *    до трёх карточек с обложками (нет картинки — фирменная обложка, NewsCover);
 *  • справа: «Быстрые действия» (отпуск, отгул, командировка — плитки «Нового процесса») и
 *    «Ближайшие события» (тот же GET /calendar/events, что у «Календаря», окно — месяц вперёд).
 *
 * Ширины резиновые: контент до 1920 на сетке из трёх равных колонок (плитки — по колонке, слева
 * блок на две, справа на одну), ряды выровнены по верху и низу. Две колонки — по измеренной
 * ширине контейнера (как «Календарь»): ширина окна не видит навигационную панель. Уже — одна
 * колонка: плитки → дни рождения → внимание → быстрые действия → события → новости. Кнопки,
 * плашки и плитки — общие компоненты «Процессов» (Button, StatusPill, ProcessTypeTile).
 * Все блоки читают живой API, пустые данные — пустое состояние.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Image } from 'react-native'
import { useRouter } from 'expo-router'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, type Lang } from '../../src/i18n'
import { STR } from '../../src/i18n/strings'
import { useResponsive } from '../../src/lib/responsive'
import { useToday } from '../../src/lib/useToday'
import { useEdoRequest, useVacationBalance } from '../../src/features/edo/useEdo'
import { useProcessList } from '../../src/features/edo/useProcessList'
import { useUnreadCount } from '../../src/features/notifications/useNotifications'
import { useBalances } from '../../src/features/profile/useProfile'
import { useNews } from '../../src/features/news/useNews'
import { NewsCover } from '../../src/features/news/NewsCover'
import { useCalendarEvents } from '../../src/features/calendar/useCalendar'
import { useMe, useUsers } from '../../src/features/users/useUsers'
import { api } from '../../src/api/client'
import { tone } from '../../src/theme/tones'
import { fmtNum, isBirthdayToday, pluralRu } from '../../src/lib/format'
import { AnimatePresence, AnimatedNumber, Avatar, Button, Card, Confetti, Enter, Icon, Link, prefersReducedMotion, PressCard, QueryState, SectionCard, Skeleton, SkeletonLines, Tag } from '../../src/ui'
import { MetricTile, type Metric } from '../../src/ui/MetricTile'
import { Page } from '../../src/ui/Screen'
import type { NewsItem } from '../../src/lib/types'
import { typeOf } from '../../src/features/edo/registry'
import { cardRoute, currentStep, docNo, routeOf, type ProcVM } from '../../src/features/edo/processVM'
import { StatusPill } from '../../src/features/edo/components/ProcessRow'
import { ProcessTypeTile } from '../../src/features/edo/components/ProcessTypeTile'
import { RouteStepper } from '../../src/features/edo/components/RouteTimeline'
import { ProcessDrawer, useOpenProcess } from '../../src/features/edo/components/ProcessDrawer'
import { PersonDrawer, liveVM, type PersonVM } from '../../src/features/users/PersonDrawer'

const LOCALE: Record<Lang, string> = { ru: 'ru-RU', en: 'en-US', az: 'az-AZ', ka: 'ka-GE' }
// Ширины резиновые (владелец, 29.10.2026: «удобно и с большого экрана, и с маленького»): контент
// тянется до PAGE_MAX. Сетка из трёх равных колонок (владелец: «боковые карточки — по сетке»):
// плитки сверху — по колонке, слева блок на две колонки, справа — на одну, ровно под третьей плиткой.
const PAGE_MAX = 1920 // шире — поля по краям (ультраширокие мониторы)
const GAP = 16
const TWO_COL_MIN = 900 // уже — одна колонка
const ROW_WIDE = 860 // строка «Требуют внимания» в одну линию от этой ширины левой колонки
const ROW_MID = 520 // уже — строка столбиком (телефон)
const clamp = (v: number, lo: number, hi: number) => Math.round(Math.min(hi, Math.max(lo, v)))
const COVER_RATIO = 2.2 // обложка новости: ширина / высота
const ATTENTION_LIMIT = 5
// Столько документов и меньше — под каждым маршрут в линию: ряд тянется по «Быстрым действиям»,
// и одна-две строки оставляли пустую карточку (владелец, 02.11.2026). Только в две колонки.
const ROUTE_MAX = 2
const ROUTE_STEP_MIN = 120 // уже — шаг маршрута не читается: длинный маршрут сворачивается в «+N»
const EVENTS_DAYS = 30
const EVENTS_LIMIT = 4

const ymdOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const DAY_MS = 86_400_000

/** «3 заявки», «2 дня»: у русского три формы, у английского две, az/ka не склоняют после числа. */
type Plural = { ru: [string, string, string]; en: [string, string]; az: string; ka: string }
const plural = (lang: Lang, n: number, f: Plural) =>
  lang === 'ru' ? pluralRu(n, ...f.ru) : lang === 'en' ? f.en[n === 1 ? 0 : 1] : f[lang]
const UNIT_DAY: Plural = { ru: ['день', 'дня', 'дней'], en: ['day', 'days'], az: 'gün', ka: 'დღე' }

const greetKey = (h: number) => (h >= 5 && h < 12 ? 'dash.morning' : h >= 12 && h < 18 ? 'dash.afternoon' : 'dash.evening')
const isActive = (p: ProcVM) => p.status === 'work' || p.status === 'review'

/** Мелкая подпись капителью над значением: «ОЖИДАЕТ», «СЕГОДНЯ ДЕНЬ РОЖДЕНИЯ». */
function Caption({ children }: { children: ReactNode }) {
  return (
    <Text fontSize={10} fontWeight="700" color="$muted" textTransform="uppercase" letterSpacing={0.6}>
      {children}
    </Text>
  )
}


// ── «Требуют внимания» ───────────────────────────────────────────────────────
/**
 * Раскладка строки по ширине колонки: `wide` — всё в линию (название | ожидает | кнопки);
 * `mid` — «ожидает» строкой под названием, кнопки справа; `narrow` (телефон) — столбиком,
 * плашка рядом с «ожидает», кнопки на всю ширину поровну.
 */
type RowMode = 'wide' | 'mid' | 'narrow'

function AttentionRow({ p, since, mode, waitW, first, onOpen }: { p: ProcVM; since?: string; mode: RowMode; waitW: number; first: boolean; onOpen: (p: ProcVM) => void }) {
  const { t, l } = useApp()
  const type = typeOf(p.type)
  // Заголовок и хвост деталей — как в ProcessRow: из meta убираем повтор названия вида.
  const titleText = t(p.title ?? type.name)
  const metaText = t(p.meta)
  const metaTail = metaText === titleText ? '' : metaText.startsWith(`${titleText} · `) ? metaText.slice(titleText.length + 3) : metaText
  const needsMe = !!p.task && !!p.canDecide
  const narrow = mode === 'narrow'
  // высота кнопок — как в «Процессах»: на узком экране 44 (палец), иначе 36
  const btnH = narrow ? 44 : 36

  // Задача на мне: «Ваше решение · 2 дня». Своя заявка: на ком шаг и «шаг 2 из 3».
  const cur = currentStep(p)
  const who = cur ? (cur.person?.name ?? (cur.group ? t(cur.group) : t(cur.act))) : ''
  const stepText = p.approvalRouteUnavailable ? '' : `${l('doc.step').toLowerCase()} ${p.step} ${l('doc.of')} ${p.steps}`
  const waitText = needsMe ? [l('proc.yourDecision'), since].filter(Boolean).join(' · ') : [who, stepText].filter(Boolean).join(' · ')

  const info = (
    <YStack minWidth={0} gap={4}>
      {/* одна строка, как в макете: детали берут только остаток после названия (flexBasis 0),
          название режется, лишь когда само не влезает в колонку */}
      <XStack alignItems="baseline" gap={6} minWidth={0}>
        <Text fontSize={14} fontWeight="700" color="$color" numberOfLines={1} flexShrink={1}>
          {titleText}
        </Text>
        {metaTail ? (
          <Text fontSize={12} color="$muted" numberOfLines={1} flexGrow={1} flexShrink={1} flexBasis={0} minWidth={0}>
            · {metaTail}
          </Text>
        ) : null}
      </XStack>
      <Text fontSize={12} color="$muted" numberOfLines={1}>
        № {docNo(p)}
        {p.author ? ` · ${p.author}` : ''}
      </Text>
    </YStack>
  )

  const waitLabel = needsMe ? l('dash.awaits') : l('dash.now')
  // плашка — та же StatusPill, что в «Процессах» (тон «в работе» для «Ждёт вас»)
  const pill = (
    <XStack alignSelf="center" flexShrink={0}>
      <StatusPill status={needsMe ? 'work' : p.status} label={needsMe ? STR['dash.waitsYou'] : p.statusLabel} />
    </XStack>
  )
  const buttons = (
    <>
      {needsMe && (
        <Button variant="primary" height={btnH} {...(narrow ? { flex: 1 } : null)} onPress={() => onOpen(p)}>
          {l('dash.approve')}
        </Button>
      )}
      <Button height={btnH} {...(narrow ? { flex: 1 } : null)} onPress={() => onOpen(p)}>
        {l('dash.open')}
      </Button>
    </>
  )
  const divider = first ? null : ({ borderTopWidth: 1, borderColor: '$borderSecondary' } as const)

  if (mode === 'wide') {
    return (
      <XStack paddingHorizontal={20} paddingVertical={14} gap={16} alignItems="center" {...divider}>
        <YStack flex={1} minWidth={0}>
          {info}
        </YStack>
        <YStack width={waitW} flexShrink={0} gap={3}>
          <Caption>{waitLabel}</Caption>
          <Text fontSize={13} color="$color" numberOfLines={1}>
            {waitText}
          </Text>
        </YStack>
        <XStack width={300} flexShrink={0} alignItems="center" justifyContent="flex-end" gap={10}>
          {pill}
          {buttons}
        </XStack>
      </XStack>
    )
  }

  if (mode === 'mid') {
    return (
      <XStack paddingHorizontal={20} paddingVertical={14} gap={16} alignItems="center" {...divider}>
        <YStack flex={1} minWidth={0} gap={6}>
          {info}
          <XStack alignItems="baseline" gap={6} minWidth={0}>
            <Caption>{waitLabel}</Caption>
            <Text fontSize={12} color="$color" numberOfLines={1} flexShrink={1}>
              {waitText}
            </Text>
          </XStack>
        </YStack>
        <XStack flexShrink={0} alignItems="center" gap={10}>
          {pill}
          {buttons}
        </XStack>
      </XStack>
    )
  }

  return (
    <YStack paddingHorizontal={16} paddingVertical={14} gap={10} {...divider}>
      {info}
      <XStack alignItems="center" justifyContent="space-between" gap={10}>
        <YStack flex={1} minWidth={0} gap={3}>
          <Caption>{waitLabel}</Caption>
          <Text fontSize={13} color="$color" numberOfLines={2}>
            {waitText}
          </Text>
        </YStack>
        {pill}
      </XStack>
      <XStack gap={10}>{buttons}</XStack>
    </YStack>
  )
}

/**
 * Маршрут документа строки — полный, из карточки GET /edo/requests/:id (в списках сервер его не
 * отдаёт). Тот же запрос и ключ кэша, что у дровера: «Открыть» потом показывает карточку сразу.
 * Нет живой заявки, маршрута или ответа — блока нет: он справочный.
 */
function AttentionRoute({ p, max }: { p: ProcVM; max: number }) {
  const q = useEdoRequest(p.live && !p.approvalRouteUnavailable ? p.reqId ?? '' : '')
  const route = useMemo(() => (q.data ? routeOf(q.data.tasks, cardRoute(q.data)) : []), [q.data])
  if (q.isLoading) {
    return (
      <YStack paddingHorizontal={20} paddingBottom={16}>
        <Skeleton height={64} borderRadius={8} />
      </YStack>
    )
  }
  if (!route.some((r) => r.state !== 'na')) return null
  return (
    <YStack paddingHorizontal={20} paddingTop={2} paddingBottom={18}>
      <RouteStepper route={route} max={max} />
    </YStack>
  )
}

function AttentionCard({ mode, waitW, routeMax, fill, onOpen }: { mode: RowMode; waitW: number; routeMax: number; fill?: boolean; onOpen: (p: ProcVM) => void }) {
  const { l, lang } = useApp()
  const router = useRouter()
  const now = useToday()
  const { taskVMs, reqVMs, queries } = useProcessList()

  // Сколько задача ждёт: createdAt живой задачи (во VM его нет — берём из сырого ответа /edo/tasks).
  const since = useMemo(() => {
    const m = new Map<string, string>()
    for (const tk of queries.tasks.data ?? []) {
      if (!tk.createdAt) continue
      const days = Math.max(0, Math.floor((now.getTime() - new Date(tk.createdAt).getTime()) / DAY_MS))
      m.set(tk.id, days === 0 ? l('dash.dueToday') : `${days} ${plural(lang, days, UNIT_DAY)}`)
    }
    return m
  }, [queries.tasks.data, now, l, lang])

  // Сначала то, что ждёт меня, затем мои заявки в работе (без тех, по которым задача уже на мне).
  const items = useMemo(() => {
    const withTask = new Set(taskVMs.map((tk) => tk.reqId).filter(Boolean))
    const mine = reqVMs.filter((r) => isActive(r) && !withTask.has(r.reqId ?? r.id))
    return [...taskVMs, ...mine].slice(0, ATTENTION_LIMIT)
  }, [taskVMs, reqVMs])

  const loading = queries.tasks.isLoading || queries.requests.isLoading
  const withRoute = !!fill && items.length <= ROUTE_MAX

  return (
    <SectionCard
      title={l('dash.attention')}
      fill={fill}
      extra={
        <Link fontSize={12} onPress={() => router.push('/processes')}>
          {l('dash.allProcesses')}
        </Link>
      }
      pad={0}
    >
      <QueryState q={queries.tasks.isError ? queries.tasks : queries.requests}>
        {loading ? (
          <YStack padding={20}>
            <SkeletonLines lines={4} />
          </YStack>
        ) : items.length === 0 ? (
          // в две колонки карточка вытянута по ряду — пустое состояние по центру её высоты
          <YStack paddingVertical={36} alignItems="center" justifyContent="center" gap={10} {...(fill ? { flex: 1 } : null)}>
            <Icon name="circle-check-big" size={26} color="$tertiary" />
            <Text fontSize={13} color="$muted">
              {l('dash.noAttention')}
            </Text>
          </YStack>
        ) : (
          items.map((p, i) =>
            withRoute ? (
              // разделитель — у обёртки: строка и её маршрут — один блок
              <YStack key={p.id} {...(i === 0 ? null : { borderTopWidth: 1, borderColor: '$borderSecondary' })}>
                <AttentionRow p={p} since={p.taskId ? since.get(p.taskId) : undefined} mode={mode} waitW={waitW} first onOpen={onOpen} />
                <AttentionRoute p={p} max={routeMax} />
              </YStack>
            ) : (
              <AttentionRow key={p.id} p={p} since={p.taskId ? since.get(p.taskId) : undefined} mode={mode} waitW={waitW} first={i === 0} onOpen={onOpen} />
            ),
          )
        )}
      </QueryState>
    </SectionCard>
  )
}

// ── Новости ──────────────────────────────────────────────────────────────────
function NewsTile({ n, fill }: { n: NewsItem; fill?: boolean }) {
  const { lang } = useApp()
  const router = useRouter()
  const cover = api.assetUrl(n.coverUrl)
  const when = new Intl.DateTimeFormat(LOCALE[lang], { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(n.publishedAt ?? n.createdAt))
  // fill (две колонки): карточка до низа блока «Новости», лишнюю высоту ряда забирает обложка
  // (владелец, 29.10.2026: «без пустого места снизу»). Иначе обложка — пропорцией ширины.
  const coverBox = fill ? { flex: 1, minHeight: 140 } : { aspectRatio: COVER_RATIO }
  return (
    <PressCard flexGrow={1} overflow="hidden" onPress={() => router.push(`/news/${n.id}`)}>
      <Stack width="100%" {...coverBox} backgroundColor="$primaryBg" alignItems="center" justifyContent="center">
        {cover ? (
          <Image source={{ uri: cover }} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} resizeMode="cover" />
        ) : (
          <NewsCover seed={n.id} />
        )}
      </Stack>
      <YStack {...(fill ? null : { flex: 1 })} padding={14} gap={10} justifyContent="space-between">
        <Text fontSize={14} fontWeight="700" color="$color" lineHeight={20} numberOfLines={3}>
          {n.title}
        </Text>
        <Text fontSize={12} color="$muted">
          {when}
        </Text>
      </YStack>
    </PressCard>
  )
}

/** `width` — ширина колонки, где стоит карточка: по ней 1/2/3 новости в ряд. */
function NewsCard({ width, fill }: { width: number | null; fill?: boolean }) {
  const { l } = useApp()
  const router = useRouter()
  const newsQuery = useNews()
  const items = Array.isArray(newsQuery.data) ? newsQuery.data.slice(0, 3) : []
  // Сетка как в «Новом процессе»: колонка = доля ширины, а не flexGrow — одна-две новости
  // не растягиваются на всю ширину огромными обложками.
  const cols = width == null || width >= 720 ? 3 : width >= 460 ? 2 : 1

  return (
    <SectionCard
      title={l('dash.news')}
      fill={fill}
      extra={
        <Link fontSize={12} onPress={() => router.push('/news')}>
          {l('dash.allNews')}
        </Link>
      }
    >
      <QueryState q={newsQuery}>
        {newsQuery.data === undefined ? (
          <XStack gap={14}>
            <Skeleton flex={1} height={200} borderRadius={7} />
            <Skeleton flex={1} height={200} borderRadius={7} />
            <Skeleton flex={1} height={200} borderRadius={7} />
          </XStack>
        ) : items.length === 0 ? (
          <YStack paddingVertical={36} alignItems="center" gap={10}>
            <Icon name="newspaper" size={26} color="$tertiary" />
            <Text fontSize={13} color="$muted">
              {l('dash.noNews')}
            </Text>
          </YStack>
        ) : (
          // fill: сетка занимает всё тело блока, ряды делят высоту (alignContent stretch)
          <XStack flexWrap="wrap" margin={-7} {...(fill ? { flex: 1, alignContent: 'stretch' as const } : null)}>
            {items.map((n) => (
              <Stack key={n.id} width={`${100 / cols}%`} padding={7}>
                <NewsTile n={n} fill={fill} />
              </Stack>
            ))}
          </XStack>
        )}
      </QueryState>
    </SectionCard>
  )
}

// ── Правая колонка ───────────────────────────────────────────────────────────
const QUICK = ['vac', 'off', 'trip'] as const

/** Те же плитки, что в «Новом процессе» (ProcessTypeTile), — без своих кнопок. */
function QuickActionsCard({ inSide }: { inSide: boolean }) {
  const { l } = useApp()
  const router = useRouter()
  return (
    <SectionCard
      title={l('dash.quick')}
      fill={inSide}
      extra={
        <Link fontSize={12} onPress={() => router.push('/processes/new')}>
          {l('dash.allProcesses')}
        </Link>
      }
    >
      {/* в правой колонке — столбиком; в одной колонке (планшет, телефон) — в ряд, сколько влезет */}
      <XStack flexWrap="wrap" gap={10}>
        {QUICK.map((id) => (
          <Stack key={id} flexGrow={1} flexBasis={inSide ? '100%' : 240}>
            <ProcessTypeTile pt={typeOf(id)} />
          </Stack>
        ))}
      </XStack>
    </SectionCard>
  )
}

function EventsCard({ fill }: { fill?: boolean }) {
  const { t, l, lang } = useApp()
  const router = useRouter()
  const today = useToday()
  const from = ymdOf(today)
  const to = ymdOf(new Date(today.getFullYear(), today.getMonth(), today.getDate() + EVENTS_DAYS))
  const q = useCalendarEvents(from, to)
  const items = useMemo(() => (q.data ?? []).slice().sort((a, b) => a.date.localeCompare(b.date)).slice(0, EVENTS_LIMIT), [q.data])
  const mon = (d: Date) => new Intl.DateTimeFormat(LOCALE[lang], { month: 'short' }).format(d).replace(/\.$/, '').toUpperCase()

  return (
    <SectionCard
      title={l('cal.upcoming')}
      fill={fill}
      extra={
        <Link fontSize={12} onPress={() => router.push('/calendar')}>
          {l('nav.calendar')}
        </Link>
      }
    >
      {q.isLoading ? (
        <SkeletonLines lines={3} />
      ) : items.length === 0 ? (
        <Text fontSize={13} color="$muted" paddingVertical={6}>
          {l('dash.noEvents')}
        </Text>
      ) : (
        <YStack gap={14}>
          {items.map((e) => {
            const d = new Date(e.date)
            return (
              <XStack key={e.id} gap={14} alignItems="flex-start">
                <YStack width={36} alignItems="center" flexShrink={0}>
                  <Text fontSize={20} fontWeight="700" color="$primary" lineHeight={22}>
                    {d.getDate()}
                  </Text>
                  <Text fontSize={10} color="$muted" marginTop={2}>
                    {mon(d)}
                  </Text>
                </YStack>
                <YStack flex={1} minWidth={0} gap={3}>
                  <Text fontSize={13} fontWeight="600" color="$color" lineHeight={18}>
                    {t(e.title)}
                  </Text>
                  <Text fontSize={11} color="$muted">
                    {t(e.kind)}
                  </Text>
                </YStack>
              </XStack>
            )
          })}
        </YStack>
      )}
    </SectionCard>
  )
}

/**
 * Дни рождения — карусель именинников (по одному за раз) поверх лёгкого
 * конфетти. Данные — общий react-query кэш `/users` (тот же, что «Сотрудники»).
 * Пока birthday у всех null (до 1С) список пуст → карточка скрыта целиком.
 */
function HomeBirthdaysCard({ onOpen }: { onOpen: (p: PersonVM) => void }) {
  const { l } = useApp()

  // Shared with the «Сотрудники» screen — same query key, no extra fetch.
  const { data: users } = useUsers()
  const celebrants = useMemo(() => (users ?? []).filter((u) => isBirthdayToday(u.birthday)), [users])

  const [idx, setIdx] = useState(0)

  // Автопрокрутка — только при нескольких именинниках и без reduce-motion.
  useEffect(() => {
    if (celebrants.length <= 1 || prefersReducedMotion()) return
    const t = setInterval(() => setIdx((v) => v + 1), 4000)
    return () => clearInterval(t)
  }, [celebrants.length])

  // Нет именинников → карточка скрыта целиком. Это же покрывает загрузку и
  // текущий прод, где birthday всюду null до подключения 1С.
  if (celebrants.length === 0) return null

  const safe = celebrants.length ? idx % celebrants.length : 0
  const person = celebrants[safe]

  return (
    <Enter index={3}>
      <SectionCard
        title={
          <XStack alignItems="center" gap={8}>
            <Icon name="gift" size={15} color={tone('gold').text} />
            <Text fontSize={14} fontWeight="700" color="$color">
              {l('dash.birthdays')}
            </Text>
          </XStack>
        }
        extra={celebrants.length > 1 ? <Tag tone="gold">{`${safe + 1} / ${celebrants.length}`}</Tag> : undefined}
      >
        <Stack position="relative" minHeight={72}>
          {/* конфетти позади, карусель поверх */}
          <Confetti count={14} />
          <AnimatePresence exitBeforeEnter>
            <YStack
              key={person.id}
              animation="quicker"
              enterStyle={{ opacity: 0, x: 24 }}
              exitStyle={{ opacity: 0, x: -24 }}
              opacity={1}
              x={0}
            >
              <XStack
                alignItems="center"
                gap={12}
                cursor="pointer"
                hoverStyle={{ opacity: 0.85 }}
                onPress={() => onOpen(liveVM(person))}
                accessibilityRole="button"
              >
                <Avatar name={person.fullName} seed={person.id} size="lg" />
                <YStack flex={1} minWidth={0}>
                  <Text fontSize={15} fontWeight="700" color="$color">
                    {person.fullName}
                  </Text>
                  <Text fontSize={12} color="$muted">
                    {person.jobTitle ?? ''}
                  </Text>
                  <Text fontSize={11} fontWeight="700" color={tone('gold').text} marginTop={2}>
                    {l('dash.birthdaysToday')}
                  </Text>
                </YStack>
              </XStack>
            </YStack>
          </AnimatePresence>

          {celebrants.length > 1 && (
            <XStack gap={6} marginTop={12}>
              {celebrants.map((c, i) => (
                <Stack
                  key={c.id}
                  width={6}
                  height={6}
                  borderRadius={999}
                  backgroundColor={i === safe ? '$primary' : '$borderColor'}
                  cursor="pointer"
                  hitSlop={6}
                  onPress={() => setIdx(i)}
                />
              ))}
            </XStack>
          )}
        </Stack>
      </SectionCard>
    </Enter>
  )
}

export default function HomeScreen() {
  const { l, lang } = useApp()
  const { isWide } = useResponsive()
  const now = useToday()
  const [sel, setSel] = useState<ProcVM | null>(null)
  const openProcess = useOpenProcess(setSel)
  const [selPerson, setSelPerson] = useState<PersonVM | null>(null)

  // Ширина контента страницы → одна или две колонки, ширина правой колонки и колонки «Ожидает»
  // (см. шапку файла и константы вверху).
  const [rowW, setRowW] = useState<number | null>(null)
  const twoCol = rowW == null ? isWide : rowW >= TWO_COL_MIN
  // Колонка сетки = ширина одной плитки (три плитки с flex 1 и два зазора). Без округления —
  // иначе край правой колонки уезжает на пиксель от края третьей плитки.
  const colW = ((rowW ?? 0) - 2 * GAP) / 3
  const sideW = colW
  const mainW = rowW == null ? null : twoCol ? 2 * colW + GAP : rowW
  const rowMode: RowMode = mainW == null ? (isWide ? 'wide' : 'narrow') : mainW >= ROW_WIDE ? 'wide' : mainW >= ROW_MID ? 'mid' : 'narrow'
  const waitW = clamp((mainW ?? 0) * 0.22, 180, 280)
  // мест в маршруте одной линией: ширина колонки минус поля карточки (2 × 20)
  const routeMax = mainW == null ? Infinity : Math.floor((mainW - 40) / ROUTE_STEP_MIN)

  const { taskVMs, queries } = useProcessList()
  const unreadQ = useUnreadCount()
  const balancesQ = useBalances()
  const vacQ = useVacationBalance()
  const { data: me } = useMe()

  // Отпускные дни — приоритет живому остатку из 1С (GET /edo/vacation/balance); если 1С
  // недоступен — фолбэк на старый /users/me/balances, затем «—». Целое рисуем числом
  // (с count-up анимацией тайла), дробь — строкой toFixed(2).
  const liveDays = vacQ.data?.source === 'zup' ? vacQ.data.balanceDays : null
  const vac = balancesQ.data?.items.find((b) => b.kind?.toUpperCase() === 'VACATION')
  const rawDays = liveDays ?? vac?.availableDays ?? vac?.totalDays ?? null
  const vacDays: number | string = rawDays == null ? '—' : fmtNum(rawDays, 2)

  // Пока источник грузится первый раз — скелетон вместо ложного нуля/«—»
  // (isLoading гаснет и на ошибке, так что вечного скелетона не будет).
  const metrics: Metric[] = [
    { key: 'tasks', label: 'stat.tasks', value: taskVMs.length, icon: 'circle-check-big', color: tone('purple'), loading: queries.tasks.isLoading },
    { key: 'unread', label: 'stat.unread', value: unreadQ.data?.count ?? 0, icon: 'bell', color: tone('gold'), loading: unreadQ.isLoading },
    { key: 'vacation', label: 'stat.vacation', value: vacDays, icon: 'palmtree', color: tone('green'), loading: rawDays == null && (vacQ.isLoading || balancesQ.isLoading) },
  ]

  // Live /users/me only — no invented data; missing name renders as "—".
  const firstName = me?.fullName?.split(' ')[0] ?? '—'
  const dateText = new Intl.DateTimeFormat(LOCALE[lang], { weekday: 'long', day: 'numeric', month: 'long' }).format(now)

  // fill — карточка тянется на высоту своего ряда сетки (только в две колонки).
  const attention = <AttentionCard mode={rowMode} waitW={waitW} routeMax={routeMax} fill={twoCol} onOpen={openProcess} />
  const news = <NewsCard width={mainW} fill={twoCol} />
  const quick = <QuickActionsCard inSide={twoCol} />
  const events = <EventsCard fill={twoCol} />

  return (
    <>
      <Page maxWidth={PAGE_MAX}>
        <YStack
          gap={GAP}
          onLayout={(e) => {
            const w = Math.round(e.nativeEvent.layout.width)
            setRowW((prev) => (prev === w ? prev : w))
          }}
        >
          <XStack alignItems="baseline" justifyContent="space-between" flexWrap="wrap" columnGap={16} rowGap={4}>
            <Text fontSize={24} fontWeight="700" color="$color">
              {l(greetKey(now.getHours()))}, {firstName}
            </Text>
            <Text fontSize={13} color="$muted">
              {dateText.charAt(0).toUpperCase() + dateText.slice(1)}
            </Text>
          </XStack>

          {/* metric tiles cascade in, then the cards follow; flexBasis 0 — ровно по колонке сетки, а не по длине подписи */}
          <XStack flexWrap="wrap" gap={GAP}>
            {metrics.map((m, i) => (
              <Enter key={m.key} index={i} flex={1} flexBasis={0} minWidth={210}>
                <MetricTile m={m} />
              </Enter>
            ))}
          </XStack>

          {/* Дни рождения — прежняя карточка-карусель, на всю ширину между плитками и «Требуют
              внимания» (владелец, 29.10.2026); нет именинников — карточка скрыта сама. */}
          <HomeBirthdaysCard onOpen={setSelPerson} />

          {twoCol ? (
            // Сетка рядами (владелец: края карточек слева и справа совпадают сверху и снизу):
            // ряд 1 — «Требуют внимания» | «Быстрые действия», ряд 2 — «Новости» | «Ближайшие события».
            // Ряд тянет обе стороны до большей высоты.
            <>
              <XStack gap={GAP} alignItems="stretch">
                <Enter index={3} flex={1} minWidth={0}>
                  {attention}
                </Enter>
                <Enter index={3} width={sideW} flexShrink={0}>
                  {quick}
                </Enter>
              </XStack>
              <XStack gap={GAP} alignItems="stretch">
                <Enter index={4} flex={1} minWidth={0}>
                  {news}
                </Enter>
                <Enter index={4} width={sideW} flexShrink={0}>
                  {events}
                </Enter>
              </XStack>
            </>
          ) : (
            <>
              <Enter index={3}>{attention}</Enter>
              <Enter index={4}>{quick}</Enter>
              <Enter index={5}>{events}</Enter>
              <Enter index={6}>{news}</Enter>
            </>
          )}
        </YStack>

        {!isWide && <Stack height={8} />}
      </Page>

      <ProcessDrawer p={sel} onClose={() => setSel(null)} />
      <PersonDrawer p={selPerson} onClose={() => setSelPerson(null)} />
    </>
  )
}
